import React, { useCallback, useMemo } from "react";
import { useAppSelector } from "@/store/index";
import { SocialChannel } from "@/database/user/model";
import { Playlist } from "./playlistData";
import {
  PlaylistYoutubeIcon,
  PlaylistChip,
  ChannelMappings,
  isOnSitePlaylist,
  FavStar,
  PIN_ACTIVE,
  PIN_IDLE,
} from "./playlistShared";
import PickerSheet, { RailEntry } from "../PickerSheet";

/** Left-rail bucket for playlists that live only on site (no YouTube channel). */
const SITE_KEY = "__site__";
/** Left-rail bucket showing every playlist across all channels. */
const ALL_KEY = "__all__";

/* ───────────── Expanded picker sheet (channel rail + that channel's playlists) ─────────────
 * Thin wrapper over the shared PickerSheet: toggling a playlist applies it immediately, same
 * as the inline picker — no staging, no footer. */
const ExpandSheet: React.FC<{
  show: boolean;
  playlists: Playlist[];
  selected: Set<string>;
  onClose: () => void;
  onApply: (next: Set<string>) => void;
  /** Opens the Manage sheet for a playlist (edit its contents / order). */
  onEdit: (id: string) => void;
  denmarkEnabled?: boolean;
  /** Favorites are owned by the parent (backed by user_preference) and shared with this sheet. */
  favorites: string[];
  onToggleFav: (id: string) => void;
  /** Primary playlist id (parent-owned); defaults to the first selected chip when unset. */
  primaryId?: string | null;
  onMakePrimary?: (id: string) => void;
}> = ({
  show,
  playlists,
  selected,
  onClose,
  onApply,
  onEdit,
  favorites,
  onToggleFav,
  primaryId,
  onMakePrimary,
}) => {
  const toggleFav = onToggleFav;

  // Connected channels — used only to borrow a channel's logo for its rail entry.
  const socialChannelsList = useAppSelector(
    (s) => s.app.userSocialChannels.socialChannelsList
  ) as SocialChannel[] | undefined;

  // Group the CUSTOM playlists by the YouTube channel they're mapped to (their youtube_playlist
  // mapping). Only channels that have at least one mapped custom playlist become rail entries, and
  // each channel's pane lists exactly those mapped custom playlists.
  const channelGroups = useMemo(() => {
    const norm = (s?: string) => (s || "").trim().toLowerCase();
    const map = new Map<
      string,
      {
        key: string;
        label: string;
        image?: string;
        items: Playlist[];
      }
    >();
    playlists.forEach((p) =>
      (p.youtubeChannels || []).forEach((ch) => {
        const channelKey = ch.channelId || ch.channelName;
        if (!channelKey) return;
        if (!map.has(channelKey)) {
          const connected = (socialChannelsList || []).find(
            (c) =>
              c.status === 1 &&
              norm(c.platform) === "youtube" &&
              (c.channel === ch.channelId ||
                norm(c.name) === norm(ch.channelName))
          );
          map.set(channelKey, {
            key: `yt:${channelKey}`,
            label: ch.channelName || channelKey,
            image: connected?.image,
            items: [],
          });
        }
        map.get(channelKey)!.items.push(p);
      })
    );
    return [...map.values()];
  }, [playlists, socialChannelsList]);

  // "All" lists every custom playlist; "On site" keeps only those with no youtube_playlist
  // mapping; each channel bucket lists the custom playlists mapped to that channel.
  const rail = useMemo<RailEntry<Playlist>[]>(() => {
    const site = playlists.filter(isOnSitePlaylist);

    // channel logo (Social Publish image); globe for on-site, YT mark as fallback
    const icon = (key: string, image?: string) =>
      key === ALL_KEY ? (
        <i
          className="bi bi-collection-play text-secondary"
          style={{ fontSize: 16 }}
        />
      ) : key === SITE_KEY ? (
        <i className="bi bi-globe2 text-secondary" style={{ fontSize: 16 }} />
      ) : image ? (
        <img
          src={image}
          alt=""
          className="rounded-circle"
          style={{ width: 20, height: 20, objectFit: "cover" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <PlaylistYoutubeIcon />
      );

    const topAndYt: RailEntry<Playlist>[] = [
      {
        key: ALL_KEY,
        label: "All",
        icon: icon(ALL_KEY),
        items: playlists,
        section: "Channels",
      },
      {
        key: SITE_KEY,
        label: "On site",
        icon: icon(SITE_KEY),
        items: site,
        section: "Channels",
      },
      ...channelGroups.map((g) => ({
        key: g.key,
        label: g.label,
        icon: icon(g.key, g.image),
        items: g.items,
        section: "Channels",
      })),
    ];

    return topAndYt;
  }, [playlists, channelGroups]);

  // Favorites float to the top; order is otherwise stable.
  const sortItems = useCallback(
    (items: Playlist[]) =>
      [...items].sort(
        (a, b) =>
          Number(favorites.includes(b.id)) - Number(favorites.includes(a.id))
      ),
    [favorites]
  );

  return (
    <PickerSheet<Playlist>
      show={show}
      selected={selected}
      onClose={onClose}
      onApply={onApply}
      rail={rail}
      defaultActiveKey={ALL_KEY}
      paneTitle="Playlists"
      searchPlaceholder="Search playlists"
      emptySearchText="No playlists match your search."
      emptyText="No playlists in this channel."
      getId={(p) => p.id}
      getSearchText={(p) => p.name}
      chipPrimaryId={primaryId}
      onChipMakePrimary={onMakePrimary}
      renderChip={(p, { isPrimary, remove }) => (
        <PlaylistChip name={p.name} primary={isPrimary} onRemove={remove} />
      )}
      sortItems={sortItems}
      checkColors={{ active: PIN_ACTIVE, idle: PIN_IDLE }}
      renderItemBody={(p) => (
        <>
          <div className="text-truncate">{p.name}</div>
          <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
            {p.videoCount ?? 0} video{(p.videoCount ?? 0) === 1 ? "" : "s"}
            {p.updatedAt && <> · Updated {p.updatedAt}</>}
          </div>
          <ChannelMappings p={p} />
        </>
      )}
      renderItemActions={(p, { isHover }) => {
        const isFav = favorites.includes(p.id);
        const showFav = isFav || isHover;
        return (
          <>
            {/* favorite star — same affordance as the inline picker */}
            <button
              type="button"
              className="btn btn-link p-0 flex-shrink-0"
              title={isFav ? "Remove from favorites" : "Add to favorites"}
              aria-label={
                isFav
                  ? `Remove ${p.name} from favorites`
                  : `Add ${p.name} to favorites`
              }
              style={{ visibility: showFav ? "visible" : "hidden" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleFav(p.id);
              }}>
              <FavStar filled={isFav} />
            </button>
          </>
        );
      }}
    />
  );
};

export default ExpandSheet;
